import Percept, {PerceptUserPropertyKeys} from '@perceptinsight/percept-js';
import {ErrorTags, EventNames, EventProperty} from 'src/lib/analytics/events';
import {UserProfile} from 'src/api/types/response';

const PERCEPT_KEY = '1a92538c0508f9168eb965bb96a03193e5ba04b4a4dbdf23cdfcec6c8c136f53';
const PERCEPT_KEY_DEV = 'fe3626deb35c0735c2eeac2531ae05418b356d42dfac7d4e0864769f4916a8e2';

const USER_PROPS_TO_TRACK = [EventProperty.Email, EventProperty.FirstName, EventProperty.LastName];

class TrackerClass {
    isInitialized = false;

    activeUserData: UserProfile | undefined;

    currentScreenName = '';
    pendingEvents: Array<{
        eventName: EventNames;
        properties?: Partial<Record<EventProperty, unknown>>;
        untypedProps?: Record<string, unknown>;
    }> = [];

    init() {
        if (this.isInitialized) return;
        const perceptKey =
            window.location.origin.includes('localhost') || window.location.origin.includes('staging')
                ? PERCEPT_KEY_DEV
                : PERCEPT_KEY;
        Percept.init(perceptKey, {
            autoTrackApiCalls: true,
            apiTrackingBlocklist: 'fonts\\.googleapis\\.com' as any,
            autoCaptureClicks: true,
            enableExperiment: false,
            enableHeatmap: true,
            enableHotspot: true,
            autoTrackRuntimeErrors: true,
        });
        this.isInitialized = true;
    }

    async setUser(userProfile: UserProfile) {
        if (!this.isInitialized) return;
        await Percept.setUserId(userProfile.username);
        Percept.setUserProperties({
            [PerceptUserPropertyKeys.EMAIL]: userProfile.email,
            [PerceptUserPropertyKeys.USER_ID]: userProfile.username,
            name: userProfile.first_name,
        });
    }

    setUserInfo(userInfo: UserProfile) {
        if (!this.isInitialized) return;
        this.activeUserData = userInfo;
        this.setUser(userInfo);
        Object.keys(userInfo)
            .filter((k) => USER_PROPS_TO_TRACK.includes(k as any))
            .forEach((key) => {
                const value = userInfo[key as keyof UserProfile]?.toString();
                this.setGlobalProperty(key as unknown as EventProperty, value);
            });
        if (this.pendingEvents.length > 0) {
            this.pendingEvents.forEach((eventData) =>
                this.trackEvent(eventData.eventName, eventData.properties, eventData.untypedProps),
            );
            this.pendingEvents = [];
        }
    }

    setCurrentScreenName(screenName: string) {
        this.currentScreenName = screenName;
    }

    setGlobalProperty(propertyKey: EventProperty, value: string) {
        if (!this.isInitialized) return;
        Percept.setGlobalProperty(propertyKey as unknown as string, value);
    }

    trackError(error: Error, tag: ErrorTags, extraProps?: Record<string, unknown>) {
        this.trackEvent(
            EventNames.ERROR,
            {
                [EventProperty.ErrorStack]: error?.stack?.toString(),
                [EventProperty.ErrorMessage]: error?.message,
                [EventProperty.ErrorTag]: tag,
            },
            extraProps,
        );
    }

    trackEvent(
        eventName: EventNames,
        properties?: Partial<Record<EventProperty, unknown>>,
        untypedProps?: Record<string, unknown>,
    ) {
        if (!this.isInitialized) {
            this.pendingEvents.push({eventName, properties, untypedProps});
            return;
        }
        const propsToCapture = {
            ...(properties ?? {}),
            ...(untypedProps ?? {}),
            appSource: 'webapp',
            screen: this.currentScreenName,
            path: location.pathname, // eslint-disable-line  no-restricted-globals
            env: 'production',
        };
        Percept.capture(eventName, {
            ...propsToCapture,
        });
    }

    logout() {
        if (!this.isInitialized) return;
        this.trackEvent(EventNames.LOGOUT);
        Percept.clear();
    }
}

const Tracker = new TrackerClass();
export default Tracker;
